<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

// Hedef kitle bilgileri
const audienceCount = ref(0);
const sendNow = ref(false);
const isSyncingTemplates = ref(false);

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

// Scheduled_at required sadece sendNow false ise
const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: sendNow.value ? {} : { required },
  selectedAudience: { required },
}));

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

// Get current inbox for template_default_images
const currentInbox = computed(() => {
  if (!state.inboxId) return null;
  return formState.inboxes.value.find(inbox => inbox.id === state.inboxId);
});

// Get default image URL for selected template
const defaultHeaderImageUrl = computed(() => {
  if (!selectedTemplate.value || !currentInbox.value) return '';
  const templateName = selectedTemplate.value.name;
  const defaultImages = currentInbox.value.template_default_images || {};
  return defaultImages[templateName] || '';
});

// Save default image for template
const handleSaveDefaultImage = async ({ templateName, imageUrl }) => {
  if (!state.inboxId || !templateName) return;

  try {
    await InboxesAPI.setTemplateDefaultImage(state.inboxId, templateName, imageUrl);
    // Refresh inboxes to get updated template_default_images
    await store.dispatch('inboxes/get');
    useAlert('Varsayılan görsel kaydedildi');
  } catch (error) {
    useAlert(error.message || 'Görsel kaydedilirken hata oluştu');
  }
};

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: !sendNow.value ? getErrorMessage('scheduledAt', 'SCHEDULED_AT') : '',
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

// Hedef kitle sayısını hesapla - gerçek etiket sayılarından
watch(() => state.selectedAudience, async (newAudience) => {
  if (newAudience && newAudience.length > 0) {
    // Seçili etiketlerin contact sayılarını topla
    let total = 0;
    for (const labelId of newAudience) {
      const label = formState.labels.value.find(l => l.id === labelId);
      if (label && label.contacts_count !== undefined) {
        total += label.contacts_count;
      }
    }
    audienceCount.value = total || newAudience.length * 100; // Fallback
  } else {
    audienceCount.value = 0;
  }
}, { deep: true });

const handleSyncTemplates = async () => {
  if (!state.inboxId) return;

  isSyncingTemplates.value = true;
  try {
    const result = await store.dispatch('inboxes/syncTemplates', state.inboxId);
    useAlert(`Şablonlar güncellendi (${result.templates_count} şablon)`);
  } catch (error) {
    useAlert(error.message || 'Şablonlar güncellenirken hata oluştu');
  } finally {
    isSyncingTemplates.value = false;
  }
};

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
  sendNow.value = false;
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;
  const templateContent = parserData?.renderedTemplate || '';

  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  // Add header image URL if template has media header
  if (parserData?.hasMediaHeader && parserData?.headerImageUrl) {
    templateParams.header_image_url = parserData.headerImageUrl;
  }

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: sendNow.value ? new Date().toISOString() : formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

const handleSubmit = async (immediate = false) => {
  sendNow.value = immediate;
  await v$.value.$validate();

  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

const handleSendNow = () => handleSubmit(true);
const handleSchedule = () => handleSubmit(false);

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSchedule">
    <!-- WhatsApp Bilgi Banner -->
    <div class="p-3 rounded-lg bg-n-green-2 border border-n-green-6">
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-green-11">💬 WhatsApp:</span>
        <span class="font-semibold text-n-green-text">Kullandıkça Öde (Limit yok)</span>
      </div>
    </div>

    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <div class="mt-1 flex items-center justify-between">
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
        </p>
        <button
          v-if="state.inboxId"
          type="button"
          class="text-xs text-n-blue-text hover:underline flex items-center gap-1"
          :disabled="isSyncingTemplates"
          @click="handleSyncTemplates"
        >
          <span v-if="isSyncingTemplates" class="animate-spin">&#8634;</span>
          <span v-else>&#8635;</span>
          {{ isSyncingTemplates ? 'Güncelleniyor...' : 'Şablonları Güncelle' }}
        </button>
      </div>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      :default-header-image-url="defaultHeaderImageUrl"
      @save-default-image="handleSaveDefaultImage"
    />

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <!-- Hedef Kitle Sayısı -->
      <div v-if="state.selectedAudience.length > 0" class="mt-1 text-xs text-n-slate-11">
        👥 Tahmini hedef kitle: <span class="font-semibold">~{{ audienceCount }} kişi</span>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Butonlar -->
    <div class="flex flex-col gap-2">
      <div class="flex gap-3 justify-between items-center w-full">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
          @click="handleCancel"
        />
        <Button
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
          class="w-full"
          type="submit"
          :is-loading="isCreating"
          :disabled="isCreating || isSubmitDisabled"
        />
      </div>
      <!-- Hemen Gönder Butonu -->
      <Button
        variant="solid"
        color="green"
        type="button"
        label="⚡ Hemen Gönder"
        class="w-full"
        :is-loading="isCreating"
        :disabled="isCreating || !state.title || !state.inboxId || !state.templateId || state.selectedAudience.length === 0"
        @click="handleSendNow"
      />
    </div>
  </form>
</template>
